import React, { useEffect, useState, useRef } from 'react'
import { Box, Heading, Text, Input, Button, VStack, HStack, useToast, useColorModeValue } from '@chakra-ui/react'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { fetchWithoutAuth } from '../apiClient'

// Capture UTMs/referrer once per session
function captureAttribution() {
  if (!sessionStorage.getItem('ek_utm_captured')) {
    const params = new URLSearchParams(window.location.search)
    const utm = {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_term: params.get('utm_term') || null,
      utm_content: params.get('utm_content') || null,
      referrer: document.referrer || null,
    }
    sessionStorage.setItem('ek_utm', JSON.stringify(utm))
    sessionStorage.setItem('ek_utm_captured', '1')
    return utm
  }
  try { return JSON.parse(sessionStorage.getItem('ek_utm') || '{}') } catch { return {} }
}

const RECAPTCHA_ACTION = 'waitlist_submit'

function WaitlistFormContent() {
  const toast = useToast()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [email, setEmail] = useState('')
  const emailRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [duplicate, setDuplicate] = useState(false)
  const [error, setError] = useState(null)
  const [captchaError, setCaptchaError] = useState(null)
  // honeypot field: keep empty in normal UX
  const [website, setWebsite] = useState('')

  // Use the app's primary blue for active buttons so it matches other 'Add' buttons
  const activeColorScheme = 'blue'

  useEffect(() => { captureAttribution() }, [])

  // Focus the email input on mount when the form is visible
  useEffect(() => {
    if (success) return
    const t = setTimeout(() => {
      try { emailRef.current?.focus() } catch (e) { /* ignore */ }
    }, 50)
    return () => clearTimeout(t)
  }, [success])

  useEffect(() => {
    if (executeRecaptcha && captchaError) setCaptchaError(null)
  }, [executeRecaptcha, captchaError])

  async function handleSubmit(e) {
    e?.preventDefault()
    setError(null)
    setDuplicate(false)

    if (!email || !email.includes('@')) { setError('Please enter a valid email'); return }
    if (!executeRecaptcha) { setCaptchaError('Captcha could not load. Please refresh and try again.'); return }

    setCaptchaError(null)
    setLoading(true)

    try {
      const token = await executeRecaptcha(RECAPTCHA_ACTION)
      if (!token) {
        setCaptchaError('Captcha did not complete. Please try again.')
        return
      }

      const utm = JSON.parse(sessionStorage.getItem('ek_utm') || '{}')
      const payload = {
        email: email.trim().toLowerCase(),
        source: 'login',
        website: website,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        utm_term: utm.utm_term,
        utm_content: utm.utm_content,
        referrer: utm.referrer,
        userAgent: navigator.userAgent,
        captchaToken: token,
      }

      const resp = await fetchWithoutAuth('/api/waitlist', { method: 'POST', body: JSON.stringify(payload) })
      const data = await resp.json().catch(() => ({}))

      if (data?.duplicate) setDuplicate(true)
      setSuccess(true)
      toast({ title: data?.duplicate ? "You're already on the list" : "You're on the list!", status: 'success', duration: 3000 })
    } catch (err) {
      console.error('Waitlist submit failed', err)
      const friendly = err?.message || 'Could not submit - please try again.'
      if (err?.code === 'WAITLIST_CAPTCHA_FAILED') {
        setCaptchaError(friendly)
        toast({ title: friendly, status: 'error', duration: 3000 })
      } else {
        setError(friendly)
        toast({ title: friendly, status: 'error', duration: 3000 })
      }
    } finally { setLoading(false) }
  }

  return (
    <Box w="100%" mb={4}> {/* Outer AuthCard already provides padding/bg */}
      {!success ? (
        <form onSubmit={handleSubmit}>
          <VStack spacing={3} align="stretch">
            <Heading size="md">Join the waitlist</Heading>
            <Text>We'll email you with updates on ekkos.</Text>
            {/* Honeypot: visually hidden input bots often fill */}
            <input
              type="text"
              name="website"
              autoComplete="off"
              tabIndex="-1"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
            <HStack spacing={3} wrap="wrap">
              <Input ref={emailRef} placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} flex={1} />
              <Button
                type="submit"
                isLoading={loading}
                isDisabled={!email || email.trim() === ''}
                aria-disabled={!email || email.trim() === ''}
                title={(!email || email.trim() === '') ? 'Enter your email to join the waitlist' : 'Join the waitlist'}
                colorScheme={email && email.trim() !== '' ? activeColorScheme : 'gray'}
                variant={email && email.trim() !== '' ? 'solid' : 'outline'}
                _hover={email && email.trim() !== '' ? { filter: 'brightness(0.95)' } : undefined}
                _active={email && email.trim() !== '' ? { transform: 'translateY(1px)' } : undefined}
                _focus={email && email.trim() !== '' ? { boxShadow: 'outline' } : undefined}
                transition="all 120ms ease"
              >
                Join
              </Button>
            </HStack>
            {captchaError && <Text color="red.300">{captchaError}</Text>}
            {error && <Text color="red.300">{error}</Text>}
            {duplicate && <Text color="yellow.300">You're already on the list.</Text>}
            <Text fontSize="sm">Emails are not sold or shared. Unsubscribe anytime.</Text>
          </VStack>
        </form>
      ) : (
        <Box>
          <Heading size="md">You're on the list!</Heading>
          <Text>If you change your mind you can unsubscribe from any message we send you.</Text>
        </Box>
      )}
    </Box>
  )
}

export default function WaitlistForm({ featureFlag = true }) {
  if (!featureFlag) return null

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LfX6t4rAAAAAJ6CAIS6RnmUNTPOz65lUXP5Gbqr'

  if (!recaptchaSiteKey) {
    return (
  <Box w="100%" mb={4}> {/* simplified error container (no nested card) */}
        <Heading size="md">Get updates</Heading>
        <Text color="red.300" mt={3}>Captcha configuration is missing. Please contact support.</Text>
      </Box>
    )
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      scriptProps={{ async: true, defer: true, appendTo: 'head' }}
    >
      <WaitlistFormContent />
    </GoogleReCaptchaProvider>
  )
}
